import React from 'react'
import { ChevronDown, Loader2, AlertCircle, Info } from 'lucide-react'
import { Checkbox } from '../../ui/checkbox'

const MeetingSelectionSection = ({
  show,
  issueTypePresent,
  isLoadingMeetings,
  meetings,
  meetingInput,
  isSubmitting,
  handleMeetingInputChange,
  meetingSuggestions,
  setMeetingSuggestions,
  showMeetingSuggestions,
  setShowMeetingSuggestions,
  selectedMeetingSuggestionIndex,
  setSelectedMeetingSuggestionIndex,
  handleMeetingSelect,
  meetingSuggestionsRef,
  errors,
  setErrors,
  formData,
  setFormData,
  isOnlineVotingTemplate,
  includeFinalised,
  onIncludeFinalisedChange,
  isDark
}) => {
  if (!show || !issueTypePresent) return null

  return (
    <>
    <div className={`border-none rounded-[16px] p-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] w-full max-w-[822px] flex flex-col gap-[16px] transition-colors duration-200 ${
      isDark ? 'bg-[#000000]' : 'bg-white'
    }`}>
      <div className="flex items-center justify-between">
        <span className={`font-medium text-[16px] leading-normal transition-colors duration-200 ${
          isDark ? 'text-white' : 'text-[#072837]'
        }`}>Select Meeting</span>
        {!isOnlineVotingTemplate && formData?.issueType === 'minutes' && (
          <label className="flex items-center gap-[8px] cursor-pointer select-none">
            <Checkbox
              id="include-finalised-meetings"
              checked={!!includeFinalised}
              onCheckedChange={(val) => onIncludeFinalisedChange(!!val)}
              disabled={isSubmitting}
              isDark={isDark}
            />
            <span className={`text-[13px] font-normal transition-colors duration-200 ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`}>
              Include Finalised Meetings
            </span>
          </label>
        )}
      </div>
      <div className="w-full" ref={meetingSuggestionsRef}>
        <div className="relative">
          <div className={`border-none rounded-[8px] p-[12px] flex items-center justify-between relative w-full transition-colors duration-200 focus-within:border-solid focus-within:border-[1px] focus-within:border-[#CD6D2C] ${
            isDark ? 'bg-[#161515]' : 'bg-[#f5f5f5] border-solid border-[1px] border-[#f8f9fa]'
          }`}>
            <input
              className={`grow basis-0 min-w-0 font-normal text-[16px] bg-transparent border-0 outline-none pr-[40px] transition-colors duration-200 ${
                isDark ? 'text-white placeholder:text-[#b3b3b3]' : 'text-[#686868] placeholder:text-[#686868]'
              }`}
              type="text"
              placeholder="Search and select meeting"
              value={meetingInput}
              disabled={isSubmitting}
              onChange={handleMeetingInputChange}
              onFocus={() => {
                if (meetingInput.length === 0) {
                  setMeetingSuggestions(meetings)
                  setShowMeetingSuggestions(meetings.length > 0)
                  setSelectedMeetingSuggestionIndex(-1)
                } else {
                  setShowMeetingSuggestions(true)
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowMeetingSuggestions(false), 150)
              }}
              autoComplete="off"
            />
            <span
              className="absolute right-[12px] top-1/2 flex items-center justify-center cursor-pointer transition-transform shrink-0"
              style={{
                transform: `translateY(-50%) rotate(${showMeetingSuggestions ? '180deg' : '0deg'})`
              }}
              onMouseDown={(e) => {
                e.preventDefault()
                if (showMeetingSuggestions) {
                  setShowMeetingSuggestions(false)
                } else {
                  setMeetingSuggestions(meetings)
                  setShowMeetingSuggestions(meetings.length > 0)
                  setSelectedMeetingSuggestionIndex(-1)
                }
              }}
            >
              {isLoadingMeetings ? (
                <Loader2 className={`w-[18px] h-[18px] animate-spin transition-colors duration-200 ${
                  isDark ? 'text-[#b3b3b3]' : 'text-[#555]'
                }`} />
              ) : (
                <ChevronDown className={`w-[18px] h-[18px] transition-colors duration-200 ${
                  meetings && meetings.length > 0
                    ? isDark ? 'text-white' : 'text-[#000]'
                    : isDark ? 'text-[#8c8c8c]' : 'text-[#898989]'
                }`} />
              )}
            </span>
          </div>

          {showMeetingSuggestions && meetingSuggestions.length > 0 && (
            <div className={`absolute w-full top-full left-0 mt-[2px] max-h-[240px] overflow-y-auto rounded-[4px] z-[1000] shadow-md transition-colors duration-200 ${
              isDark ? 'bg-[#161515] [border:1px_solid_#292929]' : 'bg-white [border:1px_solid_#ced4da]'
            }`}>
              {meetingSuggestions.map((meeting, index) => (
                <div
                  key={meeting.lMeetingRegisterID}
                  className={`px-3 py-2 cursor-pointer flex justify-between items-center group transition-colors duration-200 ${
                    index === selectedMeetingSuggestionIndex
                      ? isDark 
                        ? 'bg-[#cd6d2c]' 
                        : 'bg-[#e9ecef]'
                      : isDark 
                        ? 'bg-[#161515] hover:bg-[#292929]' 
                        : 'bg-white hover:!bg-[#072837] hover:!text-white'
                  }`}
                  onClick={() => handleMeetingSelect(meeting)}
                >
                  <div className="flex items-center gap-[6px]">
                    <span className={`font-medium text-[14px] transition-colors duration-200 ${
                      index === selectedMeetingSuggestionIndex
                        ? isDark ? 'text-[#000000]' : 'text-[#072837]'
                        : isDark ? 'text-white group-hover:!text-white' : 'group-hover:!text-white text-[#072837]'
                    }`}>
                      {meeting.MeetingTypeDescription || meeting.lMeetingRegisterID}
                    </span>
                    {meeting.lStatusID === 6 && (
                      <span className="text-[11px] font-semibold px-[6px] py-[2px] rounded-[4px] bg-[#fff5eb] text-[#cd6d2c] shrink-0">
                        Finalised
                      </span>
                    )}
                  </div>
                  <div className={`font-normal text-[12px] transition-colors duration-200 ${
                    index === selectedMeetingSuggestionIndex
                      ? isDark ? 'text-[#000000]' : 'text-[#686868]'
                      : isDark ? 'text-[#b3b3b3] group-hover:!text-white' : 'group-hover:!text-white text-[#686868]'
                  }`}>
                    {meeting.dMeeting ? (() => { const [y,m,d] = meeting.dMeeting.split('T')[0].split('-'); return `${parseInt(d)}/${parseInt(m)}/${y}`; })() : 'N/A'} {meeting.sMeetingTime || ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isOnlineVotingTemplate && (
        <div className={`flex items-start gap-[8px] rounded-[8px] p-[12px] text-[14px] leading-[1.4] transition-colors duration-200 ${
          isDark ? 'bg-[#292929] text-white' : 'bg-[#fff5eb] text-[#072837]'
        }`}>
          <Info className="w-[16px] h-[16px] flex-shrink-0 mt-[2px] text-[#CD6D2C]" />
          <span>
            If a meeting does not appear in the list, it is likely because the meeting has not been configured for online voting in Strata Master.
          </span>
        </div>
      )}

      {isOnlineVotingTemplate && formData?.selectedMeetingId && (
        <div className="flex flex-col gap-[12px]">
          <label className={`inline-flex items-center gap-[8px] text-[14px] font-normal transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-[#072837]'
          }`}>
            <Checkbox
              id="online-voting-open-for-voting"
              checked={formData?.online_voting_open_for_voting === true}
              onCheckedChange={(val)=> {
                setFormData(prev=>({ ...prev, online_voting_open_for_voting: !!val }))
              }}
              disabled={isSubmitting}
              isDark={isDark}
            />
            <span>Open for Online Voting</span>
          </label>
          <label className={`inline-flex items-center gap-[8px] text-[14px] font-normal transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-[#072837]'
          }`}>
            <Checkbox
              id="online-voting-show-on-portal"
              checked={formData?.online_voting_show_on_portal === true}
              onCheckedChange={(val)=> {
                setFormData(prev=>({ ...prev, online_voting_show_on_portal: !!val }))
              }}
              disabled={isSubmitting}
              isDark={isDark}
            />
            <span>Show on Portal</span>
          </label>
        </div>
      )}

      {!isOnlineVotingTemplate && (
        <label className={`inline-flex items-center gap-[8px] text-[16px] font-normal transition-colors duration-200 ${
          isDark ? 'text-white' : 'text-[#072837]'
        }`}>
          <Checkbox
            id="skip-meeting-update"
            checked={!!formData?.skip_meeting_update}
            onCheckedChange={(val)=> {
              setFormData(prev=>({ ...prev, skip_meeting_update: !!val }))
              if (val) setErrors(prev => ({ ...prev, meetingSuggestion: false }))
            }}
            disabled={isSubmitting}
            isDark={isDark}
          />
          <span>Skip as my meeting is already updated in Strata Master</span>
        </label>
      )}
    </div>

    {errors?.meetingSuggestion && (
      <div className="w-full max-w-[822px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#FF0000]">
        <AlertCircle className="h-[16px] w-[16px] flex-shrink-0" />
        <span className="leading-[1.4]">Please select a meeting from suggestions</span>
      </div>
    )}
    </>
  )
}

export default MeetingSelectionSection


